
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useContext,
} from "react";
import CancelButton from "../../../Components/CommonComponent/Button/CancelButton";
import SaveButton from "../../../Components/CommonComponent/Button/SaveButton";
import { useTranslation } from "react-i18next";
import googleTransliterate from "../../../utils/googleTransliterate";
import { ImCross } from "react-icons/im";
import { FaEdit, FaChevronDown } from "react-icons/fa";
import toast from "react-hot-toast";
import CreateGroup from "../../TypesAndGroups/Groups/CreateGroup";
import CreateSubGroup from "../../TypesAndGroups/SubGroups/CreateSubGroup";
import CreateLedger from "../../TypesAndGroups/Ladgers/CreateLadger";
import { CommonContext } from "../../../App";
import api from "../../../services/api";

const Dropdown = React.memo(function Dropdown({
  value,
  options,
  placeholder,
  onSelect,
  extraOption,
  hasError,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [usingKeyboard, setUsingKeyboard] = useState(false);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (opt) => { onSelect(opt); setOpen(false); setHighlightedIndex(-1); setUsingKeyboard(false); },
    [onSelect]
  );

  const handleExtraClick = useCallback(() => {
    if (extraOption?.onClick) extraOption.onClick();
    setOpen(false); setHighlightedIndex(-1); setUsingKeyboard(false);
  }, [extraOption]);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setOpen(true); setUsingKeyboard(true); setHighlightedIndex(0);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault(); setUsingKeyboard(true);
      setHighlightedIndex((prev) => prev < options.length + (extraOption ? 1 : 0) - 1 ? prev + 1 : 0);
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); setUsingKeyboard(true);
      setHighlightedIndex((prev) => prev > 0 ? prev - 1 : options.length + (extraOption ? 1 : 0) - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < options.length) handleSelect(options[highlightedIndex].value);
      else if (extraOption && highlightedIndex === options.length) handleExtraClick();
    } else if (e.key === "Escape") {
      setOpen(false); setHighlightedIndex(-1);
    }
  };

  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const item = listRef.current.children[highlightedIndex];
      if (item) item.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  return (
    <div className="relative w-full mb-1" ref={dropdownRef}>
      <div
        tabIndex={disabled ? -1 : 0}
        onClick={() => { if (disabled) return; setOpen((prev) => !prev); setUsingKeyboard(false); }}
        onKeyDown={(e) => { if (disabled) return; handleKeyDown(e); }}
        className={`border ${hasError ? "border-danger" : "border-gray-300"}
          rounded px-2 py-1 focus:outline-none flex justify-between items-center
          ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white cursor-pointer"}`}
      >
        <span className="truncate">{value || placeholder}</span>
        <FaChevronDown className={`ml-2 text-sm transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </div>

      {open && (
        <ul ref={listRef} className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded shadow max-h-40 overflow-y-auto p-0">
          {options.map((opt, i) => (
            <li
              key={opt.key}
              onClick={() => handleSelect(opt.value)}
              onMouseEnter={() => { if (!usingKeyboard) setHighlightedIndex(i); }}
              className={`px-3 py-1 cursor-pointer capitalize ${
                highlightedIndex === i
                  ? "bg-theme-headerBg text-theme-color"
                  : !usingKeyboard ? "hover:bg-theme-headerBg hover:text-theme-color" : ""
              }`}
            >
              {opt.label} {language !== "en" && opt.labelInHindi && ` ( ${opt.labelInHindi} )`}
            </li>
          ))}
          {extraOption && (
            <li
              onClick={handleExtraClick}
              onMouseEnter={() => { if (!usingKeyboard) setHighlightedIndex(options.length); }}
              className={`px-4 py-2 cursor-pointer font-medium ${
                highlightedIndex === options.length
                  ? "bg-theme-headerBg text-white"
                  : !usingKeyboard ? "text-theme-tabHoverColor hover:bg-theme-headerBg hover:text-theme-color" : "text-theme-tabHoverColor"
              }`}
            >
              {extraOption.label}
            </li>
          )}
        </ul>
      )}
    </div>
  );
});

export default function AddPayment({
  name,
  nameInHindi,
  setName,
  setNameInHindi,
  groupName,
  setGroupName,
  groupId,
  setGroupId,
  setSelectGroupValidation,
  selectGroupValidation,
  groupType,
  setGroupType,
  handleGroupSelect,
  handleSubGroupCancel,
  handleSave,         // parent se — ab { ledgerId, ledgerName } inject karke call hoga
  loading,
  setLoading,
  selectedType,
  setSelectedType,
  selectedSubGroup,
  setSelectedSubGroup,
  selectedLedger,     // parent state — name string only
  setSelectedLedger,
  typeValidation,
  onDataRefresh,
}) {
  const { t } = useTranslation();
  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";
  const { branchId } = useContext(CommonContext);

  const [showField, setShowField] = useState(false);
  const [hindiSuggestion, setHindiSuggestion] = useState("");

  const Types = [
    { name: "Assets",    nameInHindi: "सम्पत्ति, लेनदारी" },
    { name: "Liability", nameInHindi: "दायित्व, देनदारी" },
    { name: "Income",    nameInHindi: "कमाई, बिक्री" },
    { name: "Expense",   nameInHindi: "खर्च, खरीद" },
  ];

  // ✅ Group modal — alag state (main form ke groupName/groupType se alag)
  const [isGroupModalOpen, setIsGroupModalOpen]       = useState(false);
  const [addGroupName, setAddGroupName]               = useState("");
  const [groupNameInHindi, setGroupNameInHindi]       = useState("");
  const [addGroupType, setAddGroupType]               = useState(Types[0]);
  const [groupValidation, setGroupValidation]         = useState(false);
  const [groupTypeValidation, setGroupTypeValidation] = useState(false);

  // SubGroup modal
  const [isSubGroupModalOpen, setIsSubGroupModalOpen]           = useState(false);
  const [subGroupName, setSubGroupName]                         = useState("");
  const [subGroupNameInHindi, setSubGroupNameInHindi]           = useState("");
  const [subGroupValidation, setSubGroupValidation]             = useState(false);
  const [selectNewGroupValidation, setSelectNewGroupValidation] = useState(false);

  // SubGroup modal ka apna group selection (main form se alag)
  const [subGroupGroupName, setSubGroupGroupName] = useState("");
  const [subGroupGroupId, setSubGroupGroupId]     = useState("");
  const [subGroupGroupType, setSubGroupGroupType] = useState("");

  // Data
  const [allGroups, setAllGroups]     = useState([]);
  const [allSubGroups, setAllSubGroups] = useState([]);
  const [allLedgers, setAllLedgers]   = useState([]);
  const [suggestions, setSuggestions] = useState([]);
const [showSuggestions, setShowSuggestions] = useState(false);

  // ✅ Ledger ID — internal state (parent ke selectedLedger mein sirf name hai)
  const [selectedLedgerId, setSelectedLedgerId] = useState("");

  // Ledger modal
  const [modalVisible, setModalVisible] = useState(false);

  /* ================================================================
     DATA FETCHING
  ================================================================ */
  useEffect(() => {
    if (branchId) { fetchGroups(); fetchSubGroups(); fetchLedgers(); }
  }, [branchId]);

  const fetchGroups = async () => {
    try {
      const res = await api.get(`groups-types/get-all-group?branchId=${branchId}`);
      if (res.data.success) {
        setAllGroups(res.data.allData || []);
        if (onDataRefresh) onDataRefresh({ type: "groups", data: res.data.allData });
      }
    } catch (err) { console.error("Error fetching groups:", err); }
  };

  const fetchSubGroups = async () => {
    try {
      const res = await api.get(`groups-types/get-all-sub-group?branchId=${branchId}`);
      if (res.data.success) {
        setAllSubGroups(res.data.allData || []);
        if (onDataRefresh) onDataRefresh({ type: "subgroups", data: res.data.allData });
      }
    } catch (err) { console.error("Error fetching subgroups:", err); }
  };

  const fetchLedgers = async () => {
    try {
      const res = await api.get(`ledgers/get-all-ledger?branchId=${branchId}`);
      if (res.data.success) {
        setAllLedgers(res.data.allData || []);
        if (onDataRefresh) onDataRefresh({ type: "ledgers", data: res.data.allData });
      }
    } catch (err) { console.error("Error fetching ledgers:", err); }
  };

  /* ================================================================
     FILTERING
  ================================================================ */
  const getFilteredGroups = () => {
    if (!selectedType) return allGroups;
    return allGroups.filter((g) => g.type === selectedType);
  };

  const getFilteredSubGroups = () => {
    if (!groupName) return [];
    const grp = allGroups.find((g) => g.groupName === groupName);
    if (!grp) return [];
    return allSubGroups.filter((s) => s.groupId === grp.id || s.group_id === grp.id);
  };

  const getFilteredLedgers = () => {
    if (selectedSubGroup) {
      const sub = allSubGroups.find((s) => s.subGroupName === selectedSubGroup);
      if (sub) return allLedgers.filter((l) => l.subGroupId === sub.id || l.sub_group_id === sub.id);
    }
    if (groupName) {
      const grp = allGroups.find((g) => g.groupName === groupName);
      if (grp) return allLedgers.filter((l) => l.groupId === grp.id || l.group_id === grp.id);
    }
    return [];
  };

  /* ================================================================
     OPTIONS
  ================================================================ */
  const TypesOptions = useMemo(() =>
    Types.map((t) => ({ key: t.name, value: t.name, label: t.name, labelInHindi: t.nameInHindi })),
    []
  );

  const groupOptions = useMemo(() =>
    getFilteredGroups().map((g) => ({
      key: g.id || g.group_id,
      value: { name: g.groupName, id: g.id || g.group_id },
      label: g.groupName,
      labelInHindi: g.groupNameInHindi,
    })),
    [selectedType, allGroups]
  );

  const subGroupOptions = useMemo(() =>
    getFilteredSubGroups().map((sg) => ({
      key: sg.id || sg.sub_group_id,
      value: { name: sg.subGroupName, id: sg.id || sg.sub_group_id },
      label: sg.subGroupName,
      labelInHindi: sg.subGroupNameInHindi,
    })),
    [groupName, allSubGroups]
  );

  const ledgerOptions = useMemo(() =>
    getFilteredLedgers().map((l) => ({
      key: l.id || l.ledger_id,
      value: { name: l.name, id: l.id || l.ledger_id },
      label: l.name,
      labelInHindi: l.secondValue || l.cousinName,
    })),
    [selectedSubGroup, groupName, allLedgers]
  );

  const handleSelectType = useCallback((type) => {
    setSelectedType(type);
    setGroupName(""); setGroupId("");
    setSelectedSubGroup("");
    setGroupType("");
    setSelectedLedger(""); setSelectedLedgerId("");
  }, [setSelectedType, setGroupName, setGroupId, setSelectedSubGroup, setGroupType, setSelectedLedger]);

  const handleSelectGroup = useCallback((selected) => {
    setGroupName(selected.name);
    setGroupId(selected.id);
    setSelectedSubGroup("");
    setSelectedLedger(""); setSelectedLedgerId("");
    const foundGroup = allGroups.find((g) => g.id === selected.id);
    if (foundGroup) setGroupType(foundGroup.type);
    handleGroupSelect?.(selected.name);
  }, [handleGroupSelect, setGroupName, setGroupId, setSelectedSubGroup, setGroupType, setSelectedLedger, allGroups]);

  const handleSelectSubGroup = useCallback((selected) => {
    setSelectedSubGroup(selected.name);
    setSelectedLedger(""); setSelectedLedgerId("");
  }, [setSelectedSubGroup, setSelectedLedger]);

  //  Store BOTH name (display) and id (API ke liye)
  const handleSelectLedger = useCallback((selected) => {
    console.log(" Ledger selected:", selected); // debug
    setSelectedLedger(selected.name);   // parent state — sirf display
    setSelectedLedgerId(selected.id);   //  internal — payload mein jayega
  }, [setSelectedLedger]);


  const handleAddNewGroup = useCallback(() => {
    setAddGroupName("");
    setGroupNameInHindi("");
    setAddGroupType(Types[0]);
    setGroupValidation(false);
    setGroupTypeValidation(false);
    setIsGroupModalOpen(true);
  }, []);

  const handleAddNewSubGroup = useCallback(() => {
    setSubGroupName(""); setSubGroupNameInHindi("");
    setSubGroupValidation(false); setSelectNewGroupValidation(false);
    if (groupName && groupId) {
      setSubGroupGroupName(groupName);
      setSubGroupGroupId(groupId);
      setSubGroupGroupType(groupType || "");
    } else {
      setSubGroupGroupName(""); setSubGroupGroupId(""); setSubGroupGroupType("");
    }
    setIsSubGroupModalOpen(true);
  }, [groupName, groupId, groupType]);

  const handleAddNewLedger = useCallback(() => setModalVisible(true), []);


const handleNameChange = async (e) => {
  const value = e.target.value;
  setName(value);
  setHindiSuggestion(value);

  try {
    if (language !== "en") {
      const result = await googleTransliterate(value, language);

      if (result?.suggestions) {
        setSuggestions(result.suggestions.slice(0,3));
        setNameInHindi(result.suggestions[0] || "");
      } else if (typeof result === "string") {
        setSuggestions([result]);
        setNameInHindi(result);
      }

      setShowSuggestions(true);
    }
  } catch (err) {
    console.error("Transliteration error:", err);
  }
};

const handleConvertNameInHindi = async (e) => {
  const value = e.target.value;
  setHindiSuggestion(value);

  try {
    const result = await googleTransliterate(value, language);

    if (result?.suggestions) {
      setSuggestions(result.suggestions.slice(0,3));
      setNameInHindi(result.suggestions[0] || "");
    } else if (typeof result === "string") {
      setSuggestions([result]);
      setNameInHindi(result);
    }

    setShowSuggestions(true);
  } catch (err) {
    console.error("Transliteration error:", err);
  }
};


  const handleSubGroupGroupSelect = useCallback((selectedGroupName) => {
    const found = allGroups.find((g) => g.groupName === selectedGroupName);
    if (found) {
      const typeObj = Types.find((t) => t.name === found.type) || { name: found.type, nameInHindi: "" };
      setSubGroupGroupType(`${typeObj.name}${language !== "en" && typeObj.nameInHindi ? ` (${typeObj.nameInHindi})` : ""}`);
      setSubGroupGroupId(found.id || found.group_id || "");
      setSubGroupGroupName(found.groupName);
    } else {
      setSubGroupGroupType(""); setSubGroupGroupId(""); setSubGroupGroupName("");
    }
  }, [allGroups, language]);


  const handleSaveSubGroup = async () => {
    if (!branchId) { toast.error("Missing branch!"); return; }
    if (!subGroupName.trim()) { setSubGroupValidation(true); return; }
    if (!subGroupGroupId) { setSelectNewGroupValidation(true); setSubGroupValidation(false); return; }

    if (setLoading) setLoading(true);
    try {
      const res = await api.post(`groups-types/new-sub-group`, {
        subGroups: [{
          subGroupName: subGroupName.trim(),
          subGroupNameInHindi: subGroupNameInHindi.trim(),
          groupId: subGroupGroupId,
        }],
        branchId,
      });
      if (res.data.success) {
        toast.success(res.data.message || "SubGroup added successfully");
        await fetchSubGroups();
        setSelectedSubGroup(subGroupName.trim());
        setGroupName(subGroupGroupName);
        setGroupId(subGroupGroupId);
        setGroupType(subGroupGroupType);
        setSelectedLedger(""); setSelectedLedgerId("");
        setIsSubGroupModalOpen(false);
        setSubGroupName(""); setSubGroupNameInHindi("");
        setSubGroupGroupName(""); setSubGroupGroupId(""); setSubGroupGroupType("");
        setSubGroupValidation(false); setSelectNewGroupValidation(false);
      } else {
        toast.error(res.data.message || "Failed to create SubGroup");
      }
    } catch (err) {
      console.error("SubGroup creation error:", err);
      toast.error(err?.response?.data?.message || "Failed to create SubGroup");
    } finally {
      if (setLoading) setLoading(false);
    }
  };


  const handleSaveGroup = async () => {
    // ✅ addGroupName use karo — main form ka groupName nahi
    if (!addGroupName.trim()) { setGroupValidation(true); return; }
    if (!branchId) { toast.error("Missing branch!"); return; }
    if (!addGroupType) { setGroupTypeValidation(true); setGroupValidation(false); return; }

    const typeObj = typeof addGroupType === "string"
      ? Types.find((t) => t.name === addGroupType)
      : addGroupType;

    if (setLoading) setLoading(true);
    try {
      const res = await api.post(`groups-types/new-group`, {
        groupName: addGroupName,      // ✅ alag state
        groupNameInHindi,
        type: typeObj?.name,
        typeInHindi: typeObj?.nameInHindi,
        branchId,
      });
      if (res.data.success) {
        const newGroup = res.data.newData;
        const resolvedName = newGroup.groupName || newGroup.group_name || "";
        const resolvedId   = newGroup.id || newGroup.group_id || "";
        const resolvedType = newGroup.type || "";

        //  Optimistic update — dropdown immediately shows new group
        setAllGroups((prev) => [...prev, newGroup]);
        fetchGroups(); // backend sync

        //  Agar subgroup modal open hai — usmein auto-select new group
        if (isSubGroupModalOpen) {
          setSubGroupGroupName(resolvedName);
          setSubGroupGroupId(resolvedId);
          setSubGroupGroupType(resolvedType);
        }

        // Reset group modal state
        setAddGroupName(""); setGroupNameInHindi("");
        setAddGroupType(Types[0]);
        setGroupValidation(false); setGroupTypeValidation(false);
        setIsGroupModalOpen(false);
        toast.success(res.data.message || "Group added successfully");
      } else {
        toast.error(res.data.message || "Failed to add group");
      }
    } catch (err) {
      console.error("Group creation error:", err);
      toast.error(err?.response?.data?.message || "Failed to add group");
    } finally {
      if (setLoading) setLoading(false);
    }
  };


  const handleSubGroupModalCancel = useCallback(() => {
    setIsSubGroupModalOpen(false);
    setSubGroupName(""); setSubGroupNameInHindi("");
    setSubGroupGroupName(""); setSubGroupGroupId(""); setSubGroupGroupType("");
    setSubGroupValidation(false); setSelectNewGroupValidation(false);
  }, []);


  const handleInternalSave = () => {
    if (!name?.trim()) { toast.error("Please enter name"); return; }
    if (!groupId)      { toast.error("Please select a group"); return; }
    if (!selectedLedgerId) { toast.error("Please select a ledger"); return; }

    //  Full payload log — check karo kya ja rha hai
    const payload = {
      name: name.trim(),
      nameInHindi,
      groupId,
      groupName,
      groupType,
      ledgerId: selectedLedgerId,
      ledgerName: selectedLedger,
      selectedType,
      selectedSubGroup,
      branchId,
    };
    handleSave({ ledgerId: selectedLedgerId, ledgerName: selectedLedger });
  };

  /* ================================================================
     UI
  ================================================================ */
  return (
    <div className="bg-white p-2 rounded shadow-lg w-[800px]">
      {/* Title */}
      <div className="bg-theme-headerBg text-theme-color text-center rounded-1 py-[2px] mb-1">
        <div className="font-medium">{t("payamentAccount")}</div>
      </div>

      {/* Name */}
      <div className="flex gap-2 my-1 px-1 mt-2 text-sm text-start">
        <div className={`${language !== "en" ? "w-1/2" : "w-full"}`}>
          <label className="font-medium text-sm text-black">{t("name")}</label>
          <input
            type="text"
            value={name}
            onChange={handleNameChange}
            placeholder="Enter name"
            className="border border-gray-300 rounded w-full p-1 px-2 capitalize"
          />
        </div>

        {showField && (
          <div className="w-1/2">
            <label className="font-medium text-sm text-start">{t("name")}</label>
            <div className="flex mb-2">
              <input type="text" value={hindiSuggestion} onChange={handleConvertNameInHindi}
                placeholder="Enter name" className="border outline-none rounded-s w-full p-1 px-2 capitalize" />
              <div className="border px-2 ms-auto w-8 cursor-pointer rounded-e bg-white">
                <ImCross className="mt-2 text-theme-createLedgerColor" onClick={() => setShowField(false)} />
              </div>
            </div>
          </div>
        )}

        {language !== "en" && (
          <div className="w-1/2 relative">
            <label className="font-medium text-black text-start">
              {language === "hi" ? t("translateinHindi") : t("translateinPunjabi")}
            </label>
            <div className="flex mb-2">
              <input type="text" value={nameInHindi} disabled placeholder="Translate value"
                className="border cursor-not-allowed bg-gray-100 border-gray-300 rounded-s w-full p-1 px-2 capitalize" />
              <div className="border px-2 ms-auto w-8 cursor-pointer rounded-e bg-white">
                <FaEdit className="mt-2" onClick={() => setShowField(true)} />
              </div>
            </div>
{showSuggestions && suggestions.length > 0 && (
    <ul className="absolute top-full left-0 w-full bg-white border rounded shadow z-50 text-sm max-h-32 overflow-y-auto p-0">
      {suggestions.map((s,i)=>(
        <li
          key={i}
          className="px-2 py-1 cursor-pointer hover:bg-theme-headerBg hover:text-theme-color"
          onClick={()=>{
            setNameInHindi(s);
            setShowSuggestions(false);
          }}
        >
          {s}
        </li>
      ))}
    </ul>
  )}

          </div>
        )}
      </div>

      <div className="text-start text-sm px-1 mb-1">{t("connectItWithLLedger")}</div>

      {/* Type + Group */}
      <div className="flex gap-2 my-1 px-1 text-sm text-start">
        <div className="w-1/2">
          <label className="font-medium text-black">{t("selectType")}</label>
          <Dropdown
            value={selectedType
              ? `${selectedType}${language !== "en"
                  ? ` (${Types.find((t) => t.name === selectedType)?.nameInHindi || ""})`
                  : ""}`
              : ""}
            placeholder="-- Select Type --"
            hasError={typeValidation}
            options={TypesOptions}
            onSelect={handleSelectType}
          />
          {typeValidation && <div className="text-red-600 text-sm px-2">{t("pleaseSelectType")}</div>}
        </div>

        <div className="w-1/2">
          <label className="font-medium text-black">{t("selectgroupName")}</label>
          <Dropdown
            value={groupName
              ? `${groupName}${language !== "en" && groupOptions.find((o) => o.label === groupName)?.labelInHindi
                  ? ` (${groupOptions.find((o) => o.label === groupName)?.labelInHindi})` : ""}`
              : ""}
            placeholder={selectedType ? "-- Select Group --" : "Select Type First"}
            hasError={selectGroupValidation}
            options={groupOptions}
            onSelect={handleSelectGroup}
            extraOption={selectedType ? { label: t("addNewGroup"), onClick: handleAddNewGroup } : null}
            disabled={!selectedType}
          />
          {selectGroupValidation && <div className="text-red-600 text-sm px-2">{t("pleaseSelectGroup")}</div>}
        </div>
      </div>

      {/* SubGroup + Ledger */}
      <div className="flex gap-2 my-1 px-1 text-sm text-start">
        <div className="w-1/2">
          <label className="font-medium text-black">{t("selectSubGroup")}</label>
          <Dropdown
            value={selectedSubGroup || ""}
            placeholder={groupName ? "-- Select Sub Group --" : "Select Group First"}
            options={subGroupOptions}
            onSelect={handleSelectSubGroup}
            extraOption={groupName ? { label: t("addNewSubGroup"), onClick: handleAddNewSubGroup } : null}
            disabled={!groupName}
          />
        </div>

        <div className="w-1/2">
          <label className="font-medium text-black">{t("selectLedger")}</label>
          <Dropdown
            value={selectedLedger
              ? `${selectedLedger}${language !== "en" && ledgerOptions.find((o) => o.label === selectedLedger)?.labelInHindi
                  ? ` (${ledgerOptions.find((o) => o.label === selectedLedger)?.labelInHindi})` : ""}`
              : ""}
            placeholder={selectedSubGroup || groupName ? "-- Select Ledger --" : "Select Group First"}
            options={ledgerOptions}
            onSelect={handleSelectLedger}
            extraOption={selectedSubGroup || groupName ? { label: t("addNewLedger"), onClick: handleAddNewLedger } : null}
            disabled={!groupName}
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2 my-2 px-1">
        <CancelButton onClick={handleSubGroupCancel} text={t("cancel")} />
        {/* ✅ handleInternalSave — validates + injects ledgerId before calling parent */}
        <SaveButton
          onClick={handleInternalSave}
          text={loading ? t("processing") : t("save")}
          disabled={loading}
        />
      </div>

      {/* ===================== GROUP MODAL ===================== */}
      {isGroupModalOpen && (
        <div className="fixed inset-0 flex justify-center items-center bg-black/20 z-[2000]">
          <CreateGroup
            groupName={addGroupName}          // ✅ alag state
            setGroupName={setAddGroupName}    // ✅ alag setter
            groupNameInHindi={groupNameInHindi}
            setGroupNameInHindi={setGroupNameInHindi}
            groupValidation={groupValidation}
            groupType={addGroupType}          // ✅ alag state
            setGroupType={setAddGroupType}    // ✅ alag setter
            groupTypeValidation={groupTypeValidation}
            setGroupTypeValidation={setGroupTypeValidation}
            types={Types}
            handleSave={handleSaveGroup}
            handleCancel={() => {
              setIsGroupModalOpen(false);
              setGroupValidation(false); setGroupTypeValidation(false);
              setAddGroupName(""); setGroupNameInHindi(""); setAddGroupType(Types[0]);
            }}
            setGroupValidation={setGroupValidation}
            selectedLedger={null}
            addMoreButton={false}
            loading={loading}
            setLoading={setLoading}
            groupBatch={[]}
            setGroupBatch={() => {}}
          />
        </div>
      )}

      {/* ===================== SUBGROUP MODAL ===================== */}
      {isSubGroupModalOpen && (
        <div className="fixed inset-0 flex justify-center items-center bg-black/20 z-[1100]">
          <CreateSubGroup
            subGroupName={subGroupName}
            subGroupNameInHindi={subGroupNameInHindi}
            setSubGroupName={setSubGroupName}
            setSubGroupNameInHindi={setSubGroupNameInHindi}
            subGroupValidation={subGroupValidation}
            groupName={subGroupGroupName}
            setGroupName={setSubGroupGroupName}
            groupId={subGroupGroupId}
            setGroupId={setSubGroupGroupId}
            selectGroupValidation={selectNewGroupValidation}
            groupType={subGroupGroupType}
            setGroupType={setSubGroupGroupType}
            setIsGroupModalOpen={setIsGroupModalOpen}
            handleGroupSelect={handleSubGroupGroupSelect}
            handleSubGroupCancel={handleSubGroupModalCancel}
            handleSave={handleSaveSubGroup}
            loading={loading}
            setLoading={setLoading}
            allGroupsData={allGroups}        // ✅ FIXED: full array, naye groups turant dikhenge
            selectedSubGroup={null}
            setSubGroupValidation={setSubGroupValidation}
            setSelectGroupValidation={setSelectNewGroupValidation}
            addMoreButton={false}
            isEditMode={false}
            subGroupBatch={[]}
            setSubGroupBatch={() => {}}
          />
        </div>
      )}

      {/* ===================== LEDGER MODAL ===================== */}
      {modalVisible && (
        <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-[1100]">
          <div className="rounded max-w-5xl text-start max-h-[70vh] w-full shadow-lg overflow-y-auto custom-scrollbar">
            <CreateLedger
              setModalVisible={(val) => { if (val === false) setModalVisible(false); }}
              ledgerData={null}
              updateLedger={async () => { await fetchLedgers(); }}
              isEdit={false}
              initialData={{
                group: groupName,
                type: selectedType,
                subGroup: selectedSubGroup,
              }}
              allGroups={allGroups}
              allSubGroups={allSubGroups}
              addMoreBtn={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}